import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faWarehouse,
  faLocationDot,
  faPhone,
  faUser,
  faCheckCircle,
  faTimesCircle,
  faPlus,
  faEdit,
  faEye,
  faCircleNotch,
  faStar,
  faExternalLinkAlt,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import customerWarehousesService from '../../services/customerWarehousesService';
import CustomerWarehouseForm from './CustomerWarehouseForm';
import ConfirmDialog from '../common/ConfirmDialog';

/**
 * CustomerWarehouseListModal
 *
 * Fetches and displays all warehouses for a given customer.
 * Props:
 * - isOpen: controls visibility
 * - onClose: close callback
 * - customerId: customer id to fetch warehouses for
 * - customerName: displayed in the header
 */
const CustomerWarehouseListModal = ({ isOpen, onClose, customerId, customerName }) => {
  const { t } = useTranslation();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [viewingWarehouse, setViewingWarehouse] = useState(null);
  const [updatingDefaultId, setUpdatingDefaultId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(0);

  const fetchTrackerRef = React.useRef({ id: null, ref: null });

  const fetchWarehouses = useCallback((id, refreshVal) => {
    if (!id) return;
    
    // Check if we are already fetching for this specific state
    if (fetchTrackerRef.current.id === id && fetchTrackerRef.current.ref === refreshVal) {
      return;
    }
    fetchTrackerRef.current = { id, ref: refreshVal };

    setLoading(true);
    setError(null);
    customerWarehousesService.getWarehouses(id)
      .then((result) => {
        if (result.success) {
          setWarehouses(Array.isArray(result.data) ? result.data : []);
        } else {
          setError(result.error || t('customerWarehouses.errors.loadFailed'));
        }
      })
      .catch(() => setError(t('customerWarehouses.errors.loadFailed')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    if (isOpen && customerId) {
      setWarehouses([]);
      fetchWarehouses(customerId, lastRefreshed);
    }
    
    // Cleanup to reset tracker when modal closes so it fetches fresh next time
    // But ONLY if it actually closes (not just strict mode re-run)
    return () => {
      // In StrictMode this runs, but we don't want to clear the tracker unless we actually mean it
      // Let's rely on isOpen or customerId change to reset it if needed.
    };
  }, [isOpen, customerId, lastRefreshed, fetchWarehouses]);

  // Handle actual modal close resetting the tracker
  useEffect(() => {
    if (!isOpen) {
      fetchTrackerRef.current = { id: null, ref: null };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div
          className="flex items-center justify-between border-b px-6 py-5"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.12),
                color: UI_COLORS.BUTTONS.PRIMARY.background,
              }}
            >
              <FontAwesomeIcon icon={faWarehouse} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                {t('customerWarehouses.headings.title')}
              </p>
              <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {customerName || 'Warehouses'}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent"
                style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.4) }}
              />
              <p className="mt-3 text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('customerWarehouses.messages.loading')}</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FontAwesomeIcon icon={faTimesCircle} size="2x" style={{ color: UI_COLORS.STATUS.ERROR }} />
              <p className="mt-3 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{error}</p>
            </div>
          ) : warehouses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FontAwesomeIcon icon={faWarehouse} size="2x" style={{ color: UI_COLORS.TEXT.MUTED }} />
              <p className="mt-3 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('customerWarehouses.messages.noWarehousesFound')}</p>
              <p className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('customerWarehouses.messages.noWarehousesAssigned')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                {warehouses.length} warehouse{warehouses.length !== 1 ? 's' : ''}
              </p>
              {warehouses.map((wh) => (
                <WarehouseCard 
                  key={wh.id} 
                  warehouse={wh} 
                  onEdit={() => setEditingWarehouse(wh)}
                  onView={() => setViewingWarehouse(wh)}
                  onSetDefault={async () => {
                    setUpdatingDefaultId(wh.id);
                    try {
                      const result = await customerWarehousesService.setDefaultWarehouse(customerId, wh.id);
                      if (result.success) {
                        setLastRefreshed(Date.now());
                      } else {
                        // Use alert (not setError) so a transient action failure doesn't
                        // replace the whole warehouse list — setError renders full-screen.
                        alert(result.error || t('common.errors.generic', 'Could not set the default warehouse.'));
                      }
                    } catch (e) {
                      // finally guarantees the row spinner clears even on a thrown error.
                      alert(e?.message || t('common.errors.generic', 'Could not set the default warehouse.'));
                    } finally {
                      setUpdatingDefaultId(null);
                    }
                  }}
                  onDelete={() => setConfirmDeleteId(wh.id)}
                  isUpdatingDefault={updatingDefaultId === wh.id}
                  isDeleting={deletingId === wh.id}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 border-t px-6 py-4"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-5 py-2.5 text-sm font-semibold transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: 'transparent',
              color: UI_COLORS.TEXT.MUTED,
              border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
            }}
          >
            {t('common.buttons.close')}
          </button>
          <button
            type="button"
            onClick={() => setIsFormOpen(true)}
            className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
              color: UI_COLORS.BUTTONS.PRIMARY.text,
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            {t('customerWarehouses.buttons.addWarehouse')}
          </button>
        </div>

        {/* Add/Edit Warehouse Form */}
        {(isFormOpen || editingWarehouse) && (
          <CustomerWarehouseForm
            customerId={customerId}
            isOpen={true}
            initialData={editingWarehouse}
            onClose={() => {
              setIsFormOpen(false);
              setEditingWarehouse(null);
              setLastRefreshed(Date.now());
            }}
          />
        )}

        {/* View Warehouse Modal */}
        {viewingWarehouse && (
          <ViewWarehouseModal
            warehouse={viewingWarehouse}
            onClose={() => setViewingWarehouse(null)}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={!!confirmDeleteId}
          title={t('common.actions.deleteWarehouse')}
          message={t('customerWarehouses.messages.deleteConfirm', { name: warehouses.find(w => w.id === confirmDeleteId)?.name || '' })}
          confirmLabel={t('common.buttons.delete')}
          cancelLabel={t('common.buttons.cancel')}
          loading={deletingId === confirmDeleteId}
          loadingLabel={t('common.buttons.deleting')}
          onConfirm={async () => {
            const idToDelete = confirmDeleteId;
            setDeletingId(idToDelete);
            try {
              const result = await customerWarehousesService.deleteWarehouse(customerId, idToDelete);
              if (result.success) {
                setLastRefreshed(Date.now());
                setConfirmDeleteId(null);
              } else {
                // A 409 (warehouse still referenced) was silently swallowed — the dialog
                // just sat there. Close it and surface why via alert (not setError, which
                // would replace the whole list).
                setConfirmDeleteId(null);
                alert(result.error || t('common.errors.generic', 'Could not delete this warehouse.'));
              }
            } catch (e) {
              // Thrown/network error previously left the spinner stuck forever.
              setConfirmDeleteId(null);
              alert(e?.message || t('common.errors.generic', 'Could not delete this warehouse.'));
            } finally {
              setDeletingId(null);
            }
          }}
          onCancel={() => setConfirmDeleteId(null)}
          confirmVariant="danger"
        />
      </div>
    </div>
  );
};

/** Single warehouse card */
const WarehouseCard = ({ warehouse, onEdit, onView, onSetDefault, onDelete, isUpdatingDefault, isDeleting }) => {
  const { t } = useTranslation();
  const loc = warehouse.location || {};
  const addressParts = [loc.delivery_location, loc.city, loc.state, loc.country].filter(Boolean);
  const fullAddress = addressParts.join(', ') || warehouse.address || null;

  return (
    <div
      className="rounded-xl border p-5 transition-shadow hover:shadow-md"
      style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
    >
      {/* Top row: name + badges */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
            style={{
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.1),
              color: UI_COLORS.BUTTONS.PRIMARY.background,
            }}
          >
            <FontAwesomeIcon icon={faWarehouse} />
          </div>
          <div className="min-w-0">
            <p className="font-semibold truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {warehouse.name}
            </p>
            {fullAddress && (
              <p className="text-xs mt-0.5 truncate" style={{ color: UI_COLORS.TEXT.MUTED }}>
                <FontAwesomeIcon icon={faLocationDot} className="mr-1" />
                {fullAddress}
              </p>
            )}
          </div>
        </div>

        {/* Badges */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {warehouse.is_default && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING || '#f59e0b', 0.12),
                color: UI_COLORS.STATUS.WARNING || '#d97706',
              }}
            >
              <FontAwesomeIcon icon={faStar} />
              Default
            </span>
          )}
          <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
            style={{
              backgroundColor: colorHelpers.withOpacity(
                warehouse.is_active ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR,
                0.1
              ),
              color: warehouse.is_active ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR,
            }}
          >
            <FontAwesomeIcon icon={warehouse.is_active ? faCheckCircle : faTimesCircle} />
            {warehouse.is_active ? t('common.statusValues.active') : t('common.statusValues.inactive')}
          </span>
        </div>
      </div>

      {/* Detail row */}
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
        {(warehouse.contact_person || warehouse.contact_phone) && (
          <div className="flex items-center gap-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            <FontAwesomeIcon icon={faUser} className="flex-shrink-0" style={{ color: UI_COLORS.TEXT.MUTED }} />
            <div className="min-w-0">
              {warehouse.contact_person && (
                <p className="truncate font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{warehouse.contact_person}</p>
              )}
              {warehouse.contact_phone && (
                <p className="text-xs truncate" style={{ color: UI_COLORS.TEXT.MUTED }}>
                  <FontAwesomeIcon icon={faPhone} className="mr-1" />
                  {warehouse.contact_phone}
                </p>
              )}
            </div>
          </div>
        )}

        {loc.state && (
          <div className="flex items-center gap-2 text-sm">
            <FontAwesomeIcon icon={faLocationDot} className="flex-shrink-0" style={{ color: UI_COLORS.TEXT.MUTED }} />
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('customerWarehouses.labels.stateCity')}</p>
              <p className="truncate font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {[loc.city, loc.state].filter(Boolean).join(', ')}
              </p>
            </div>
          </div>
        )}

        {loc.map_url && (
          <div className="flex items-center">
            <a
              href={loc.map_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all hover:scale-105"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.1),
                color: UI_COLORS.NAVIGATION.ACTIVE,
              }}
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
              {t('customerWarehouses.buttons.viewOnMap')}
            </a>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="mt-5 flex items-center justify-end gap-2 border-t pt-4" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
        {!warehouse.is_default && (
          <button
            type="button"
            onClick={onSetDefault}
            disabled={isUpdatingDefault}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all hover:scale-105 disabled:opacity-50"
            style={{
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING || '#f59e0b', 0.1),
              color: UI_COLORS.STATUS.WARNING || '#d97706',
            }}
          >
            {isUpdatingDefault ? (
              <FontAwesomeIcon icon={faCircleNotch} spin />
            ) : (
              <FontAwesomeIcon icon={faStar} />
            )}
            {t('customerWarehouses.buttons.setAsDefault')}
          </button>
        )}
        <button
          type="button"
          onClick={onView}
          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all hover:scale-105"
          style={{
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.1),
            color: UI_COLORS.NAVIGATION.ACTIVE,
          }}
        >
          <FontAwesomeIcon icon={faEye} />
          {t('common.buttons.view')}
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all hover:scale-105"
          style={{
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.1),
            color: UI_COLORS.BUTTONS.PRIMARY.background,
          }}
        >
          <FontAwesomeIcon icon={faEdit} />
          {t('common.buttons.edit')}
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={isDeleting}
          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all hover:scale-105 disabled:opacity-50"
          style={{
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
            color: UI_COLORS.STATUS.ERROR,
          }}
        >
          {isDeleting ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            <FontAwesomeIcon icon={faTrash} />
          )}
          {t('common.buttons.delete')}
        </button>
      </div>
    </div>
  );
};

/** Detail View Modal */
const ViewWarehouseModal = ({ warehouse, onClose }) => {
  const { t } = useTranslation();
  const loc = warehouse.location || {};
  const addressParts = [loc.delivery_location, loc.city, loc.state, loc.country].filter(Boolean);
  const fullAddress = addressParts.join(', ') || warehouse.address || t('common.values.notAvailable');

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <h3 className="text-lg font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('customerWarehouses.headings.details')}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('common.formFields.labels.warehouseName')}</label>
            <p className="font-semibold text-gray-800">{warehouse.name}</p>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('common.formFields.labels.fullAddress')}</label>
            <p className="text-sm text-gray-600">{fullAddress}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('common.formFields.labels.contactPerson')}</label>
              <p className="text-sm font-medium text-gray-700">{warehouse.contact_person || t('common.values.notAvailable')}</p>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('common.formFields.labels.phoneNumber')}</label>
              <p className="text-sm font-medium text-gray-700">{warehouse.contact_phone || t('common.values.notAvailable')}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('common.formFields.labels.status')}</label>
              <div className="mt-1">
                 <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${warehouse.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                   {warehouse.is_active ? t('common.statusValues.activeUpper') : t('common.statusValues.inactiveUpper')}
                 </span>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('common.formFields.labels.default')}</label>
              <div className="mt-1">
                 <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${warehouse.is_default ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                   {warehouse.is_default ? t('common.values.yesUpper') : t('common.values.noUpper')}
                 </span>
              </div>
            </div>
          </div>
          {loc.map_url && (
            <a 
              href={loc.map_url} 
              target="_blank" 
              rel="noreferrer"
              className="mt-2 block w-full rounded-xl bg-gray-50 p-3 text-center text-xs font-semibold text-blue-600 hover:bg-gray-100"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" />
              {t('customerWarehouses.buttons.openInMaps')}
            </a>
          )}
        </div>
        <div className="bg-gray-50 px-6 py-4 flex justify-end">
          <button 
            onClick={onClose}
            className="rounded-xl px-5 py-2 text-sm font-bold text-white shadow-lg"
            style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background }}
          >
            {t('common.buttons.gotIt')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerWarehouseListModal;
